// src/store/modules/presence.js
import presenceClient from '@/services/presence/PresenceClient'

const state = () => ({
  client: presenceClient,
  ws: {
    connected: false
  },
  me: {
    deviceId: null,
    customerId: null,
    userId: null
  },
  usersById: {},
  lastSnapshotAt: 0,
  lastError: null
})

const mutations = {
  SET_WS(state, patch) {
    state.ws = { ...state.ws, ...patch }
  },
  SET_ME(state, patch) {
    state.me = { ...state.me, ...patch }
  },
  SET_SNAPSHOT(state, { customer_id, users }) {
    const map = {}
    for (const u of users || []) {
      map[String(u.user_id)] = u
    }
    state.usersById = map
    state.lastSnapshotAt = Date.now()
    if (customer_id) state.me.customerId = customer_id
  },
  UPSERT_USER(state, u) {
    if (!u || !u.user_id) return
    state.usersById = {
      ...state.usersById,
      [String(u.user_id)]: u
    }
  },
  SET_ERROR(state, err) {
    state.lastError = err
  }
}

let wired = false
let pendingInitialPresence = null

const actions = {
  connect({ commit, rootState }) {
    const token = localStorage.getItem('token')
    const url = process.env.VUE_APP_PRESENCE_WS || 'wss://presence.dev.talkvox.com/ws'
    if (!token) return

    // capture whatever the UI says our presence should be right now
    pendingInitialPresence = rootState && rootState.phone && rootState.phone.ui ? rootState.phone.ui.presence : null

    if (!wired) {
      wired = true

      presenceClient.on('ws:open', () => {
        // socket open != authenticated/ready
        commit('SET_WS', { connected: false })
      })

      presenceClient.on('ws:close', () => {
        commit('SET_WS', { connected: false })
      })

      presenceClient.on('presence:hello', (m) => {
        commit('SET_ME', {
          deviceId: m.device_id || null,
          customerId: m.customer_id || null,
          userId: m.user_id || null
        })

        // now we can call it "connected"
        commit('SET_WS', { connected: true })

        // if we had an initial presence queued, set it once after hello
        // NOTE: this is for your manual/user-set presence (busy/away/dnd/etc).
        // PBX on_call/ringing will still override aggregate when applicable.
        const initial = pendingInitialPresence
        pendingInitialPresence = null
        if (initial) {
          setTimeout(() => {
            presenceClient.set(initial, '')
          }, 150)
        }
      })

      presenceClient.on('presence:snapshot', (m) => {
        commit('SET_SNAPSHOT', { customer_id: m.customer_id, users: m.users })
      })

      presenceClient.on('presence:update', (u) => {
        // server sends a flat user payload (not wrapped)
        commit('UPSERT_USER', u)
      })

      presenceClient.on('presence:error', (m) => {
        commit('SET_ERROR', m)
      })
    }

    presenceClient.connect({ url, token })
  },

  disconnect({ commit }) {
    presenceClient.disconnect()
    commit('SET_WS', { connected: false })
  },

  setMyPresence({ commit }, { status, message }) {
    commit('SET_ERROR', null)
    presenceClient.set(status, message || '')
  },

  refresh() {
    presenceClient.getSnapshot()
  }
}

const getters = {
  isConnected: (s) => !!s.ws.connected,
  users: (s) => Object.values(s.usersById || {}),
  byUserId: (s) => (id) => s.usersById[String(id)] || null,

  myPresence: (s) => {
    const id = s.me.userId
    if (!id) return null
    return s.usersById[String(id)] || null
  },

  myStatus: (_, g) => {
    const me = g.myPresence
    return String((me && me.status) ? me.status : 'offline').toLowerCase()
  },

  myLabel: (_, g) => {
    const st = g.myStatus

    // PBX-driven (canonical)
    if (st === 'on_call' || st === 'oncall') return 'On Call'
    if (st === 'ringing' || st === 'ring') return 'Ringing'

    // User/manual
    if (st === 'available') return 'Available'
    if (st === 'away') return 'Away'
    if (st === 'busy') return 'Busy'
    if (st === 'do_not_disturb' || st === 'dnd' || st === 'do not disturb') return 'Do Not Disturb'
    if (st === 'offline') return 'Offline'

    return st ? st.charAt(0).toUpperCase() + st.slice(1) : 'Offline'
  },

  myTone: (_, g) => {
    const st = g.myStatus

    // PBX-driven (show as blue in UI)
    if (st === 'on_call' || st === 'oncall') return 'call'
    if (st === 'ringing' || st === 'ring') return 'call'

    // User/manual
    if (st === 'available') return 'good'
    if (st === 'away') return 'warn'
    if (st === 'busy' || st === 'do_not_disturb' || st === 'dnd') return 'bad'

    return 'off'
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
